import React, { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  Loading, Empty, SectionHeader, Card, SubBadge,
  PLANS, SUB_STATUSES, PLAN_LABEL, SUB_LABEL, downloadCsv, ExportButton, fmtDate,
} from "./shared";
import { AdminOrgCredits } from "@/components/LodgeCredits";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

export default function AdminSubscriptions() {
  const [orgs, setOrgs] = useState(null);
  const [draft, setDraft] = useState({}); // orgId -> {plan,status}
  const [confirm, setConfirm] = useState(null); // {org, plan, status}
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/organizations").then((r) => {
      setOrgs(r.data.organizations || []);
      const d = {};
      (r.data.organizations || []).forEach((o) => { d[o.id] = { plan: o.subscription.plan, status: o.subscription.status }; });
      setDraft(d);
    }).catch(() => setOrgs([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (orgs === null) return <Loading />;

  const exportCsv = () => downloadCsv(
    "subscriptions.csv",
    ["Organisation", "Plan", "Statut", "Mis à jour le"],
    orgs.map((o) => [o.name, o.subscription.plan, o.subscription.status, o.subscription.updated_at || ""]),
  );

  const setField = (id, field, value) => setDraft((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));

  const apply = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/organizations/${confirm.org.id}/subscription`, { plan: confirm.plan, status: confirm.status });
      toast.success(`Abonnement mis à jour · ${confirm.org.name}`);
      setConfirm(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail, err.message)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-subscriptions">
      <div className="mb-5 flex items-start justify-between gap-4">
        <SectionHeader title="Subscriptions" subtitle="Gestion manuelle des abonnements par organisation (aucun paiement automatisé)." />
        <ExportButton onClick={exportCsv} testid="export-subscriptions" />
      </div>
      {orgs.length === 0 ? <Empty label="Aucune organisation." testid="subs-empty" /> : (
        <Card>
          <AdminOrgCredits orgs={orgs} />
        </Card>
      )}
      {orgs.length === 0 ? null : (
        <Card>
          <SelectableTable rows={orgs} rowId={(o)=>o.id} filename="adminsubscriptions.csv" ariaLabel="Tableau — sélection et défilement">
            <table className="w-full min-w-[760px]">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><th className="px-2 py-2"><SelectAllHeaderCheckbox /></th>
                <th className="px-5 py-3">Organisation</th><th className="px-5 py-3">Actuel</th>
                <th className="px-5 py-3">Plan</th><th className="px-5 py-3">Statut</th><th className="px-5 py-3"></th>
              </tr></thead>
              <tbody>
                {orgs.map((o) => {
                  const dr = draft[o.id] || {};
                  const changed = dr.plan !== o.subscription.plan || dr.status !== o.subscription.status;
                  return (
                    <tr key={o.id} data-testid={`sub-row-${o.id}`} className="border-b border-slate-100 last:border-0"><td className="px-2 py-2"><RowCheckbox id={o.id} /></td>
                      <td className="px-5 py-3 text-sm font-medium text-[#17201A]">{o.name}</td>
                      <td className="px-5 py-3 text-sm text-slate-600">{PLAN_LABEL[o.subscription.plan]} · <SubBadge status={o.subscription.status} /></td>
                      <td className="px-5 py-3">
                        <Select value={dr.plan} onValueChange={(v) => setField(o.id, "plan", v)}>
                          <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`sub-plan-${o.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{PLANS.map((p) => <SelectItem key={p} value={p} className="text-xs">{PLAN_LABEL[p]}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3">
                        <Select value={dr.status} onValueChange={(v) => setField(o.id, "status", v)}>
                          <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`sub-status-${o.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{SUB_STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{SUB_LABEL[s]}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-3">
                        <button data-testid={`sub-save-${o.id}`} disabled={!changed}
                          onClick={() => setConfirm({ org: o, plan: dr.plan, status: dr.status })}
                          className="rounded-lg bg-[#1E3D2B] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2C5540] disabled:opacity-40">
                          Enregistrer
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SelectableTable>
        </Card>
      )}
      <ConfirmDialog
        open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}
        title="Modifier l'abonnement"
        description={confirm ? `Confirmer le passage de « ${confirm.org.name} » au plan ${PLAN_LABEL[confirm.plan]} (statut : ${SUB_LABEL[confirm.status]}) ? Cette action est journalisée.` : ""}
        confirmLabel="Confirmer" destructive={confirm?.status === "suspended" || confirm?.status === "cancelled"}
        loading={saving} onConfirm={apply}
      />
    </div>
  );
}
